import {Component, Input, OnInit, OnDestroy, ViewChild} from '@angular/core';
import {Table} from 'primeng/table';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {GarantiasIdoneasModalidadesService} from '../../../_services/garantias-idoneas-modalidades.service';
import {GarantiasIdoneasModalidades} from '../../../_models/garantias-idoneas-modalidades';
import {GarantiasIdoneasModalidadesHistorialesCambiosService} from '../../../_services/garantias-idoneas-modalidades-historiales-cambios.service';
import {GarantiaIdoneaModalidadHistorialCambio} from '../../../_models/garantia-idonea-modalidad-historial-cambio';
import {ListaService} from '../../../_services/lista.service';
import {ListaItem} from '../../../_models/lista-item';
import {GarantiaService} from '../../../_services/garantia.service';
import {OtraGarantiaTree, TipoGarantiaTree} from '../../../_models/tipo-garantia';
import {of, Subscription} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {CodigosGarantias} from "../../../_enums/enum";
import {PermisoSeccionControl} from '../../../_models/permiso-seccion-control';
import {AutenticacionService} from '../../../_services/autenticacion.service';
import moment from 'moment/moment';
import {ConstanteService} from "../../../_services/constante.service";
import {DineroPipe} from "../../../_pipes/dinero.pipe";

@Component({
  selector: 'app-admgar-garantia-idonea',
  templateUrl: 'admgar-garantia-idonea.component.html',
  styleUrls: ['admgar-garantia-idonea.component.scss']
})
export class AdmgarGarantiaIdoneaComponent implements OnInit, OnDestroy {

  @ViewChild('dt', {static: true}) tabla: Table;

  registros: GarantiasIdoneasModalidades[] = [];
  registrosFiltrados: GarantiasIdoneasModalidades[] = [];
  cargando = true;
  columnas: any[];
  busqueda = '';

  /**
   * Sección ADMGRI con sus controles
   */
  @Input() seccionListado: any = null;

  /** Resuelve el control de la sección garantía idónea por su código */
  private ctrl(codigo: string): PermisoSeccionControl | null {
    return this.seccionListado?.controles?.[codigo] ?? null;
  }

  /** Muestra el botón Agregar si el control AGREGA está habilitado y es visible */
  get mostrarBtnAgregar(): boolean {
    const c = this.ctrl('AGREGA');
    return c && !!c.editable;
  }

  /**
   * Muestra el icono de acciones en la fila si el rol tiene EDIGAR o CONHIS habilitado
   */
  get mostrarBtnAcciones(): boolean {
    const editar = this.ctrl('EDIGAR');
    const historial = this.ctrl('CONHIS');
    return !!editar?.editable || !!historial?.editable;
  }

  /** Muestra la pestaña Configuración del modal si el control EDIGAR está habilitado. */
  get mostrarTabConfiguracion(): boolean {
    const c = this.ctrl('EDIGAR');
    return c && !!c.editable;
  }

  /** Muestra la pestaña Historial del modal si el control CONHIS está habilitado. */
  get mostrarTabHistorial(): boolean {
    const c = this.ctrl('CONHIS');
    return c && !!c.editable;
  }

  mostrarModal = false;
  modoEdicion = false;
  guardando = false;
  submitted = false;
  tabEditarActiva = 0;
  tabAnterior = 0;

  registroEditando: GarantiasIdoneasModalidades | null = null;
  historialCambios: any[] = [];
  cargandoHistorial = false;
  errorDuplicado = false;
  errorMinMaxLeasing = false;

  formulario: FormGroup;

  lisModalidades: SelectItem[] = [];
  lisTiposGarantia: SelectItem[] = [];
  lisClasesGarantia: SelectItem[] = [];
  lisBienesDerechos: SelectItem[] = [];

  lisClasesGarantiaTodas: OtraGarantiaTree[] = [];

  codigosGarantias = CodigosGarantias;
  salario = 0;

  /** Etiquetas legibles para cada campo del formulario que se audita en el historial de cambios */
  private readonly HISTORIAL_CAMPO_LABEL: Record<string, string> = {
    idModalidadGarantia: 'Modalidad',
    idTipoGarantia: 'Tipo garantía',
    idOtraGarantia: 'Clase garantía',
    idBienDeDerecho: 'Bien / Derecho',
    endeudamientoSMLV: 'Endeudamiento total obligatorio (SMLV)',
    maximoFinanciar: '(%) Máximo financiar (avalúo-precio)',
    minimoCompraLeasing: '(%) Mínimo opción compra leasing',
    maximoCompraLeasing: '(%) Máximo opción compra leasing',
    indicadorHabilitado: 'Habilitado',
  };

  /** Árbol de tipos de garantía cargado para la modalidad seleccionada */
  private arbolTipos: TipoGarantiaTree[] = [];
  private subscripciones: Subscription[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private confirmacionServicio: ConfirmationService,
    private garantiaIdoneaService: GarantiasIdoneasModalidadesService,
    private historialService: GarantiasIdoneasModalidadesHistorialesCambiosService,
    private lisService: ListaService,
    private garantiaService: GarantiaService,
    private conConstateService: ConstanteService,
    private dineroPipe: DineroPipe,
  ) {}

  ngOnInit(): void {
    this.configurarColumnas();
    this.cargarDatos();
    this.cargarListas();
    this.constanteSalario();
  }

  ngOnDestroy(): void {
    this.subscripciones.forEach(s => s.unsubscribe());
  }

  async constanteSalario() {
    const constSMLV = await this.conConstateService.getByCode('CSMMLV').toPromise();
    if (constSMLV && constSMLV.valor && constSMLV.indicadorHabilitado) {
      this.salario = Number(constSMLV.valor);
    }
  }

  private configurarColumnas(): void {
    this.columnas = [
      { field: 'modalidadGarantia', header: 'ADMGAR.LBL_MODALIDAD', sort: false },
      { field: 'tipoGarantia', header: 'ADMGAR.LBL_TIPO_GARANTIA', sort: false },
      { field: 'otraGarantia', header: 'ADMGAR.LBL_CLASE_GARANTIA', sort: false },
      { field: 'bienDeDerecho', header: 'ADMGAR.LBL_BIEN_DERECHO', sort: false },
      { field: 'endeudamientoSMLV', header: 'ADMGAR.LBL_ENDEUDAMIENTO_SMLV', sort: false },
      { field: 'maximoFinanciar', header: 'ADMGAR.LBL_MAX_FINANCIAR', sort: false },
      { field: 'minimoCompraLeasing', header: 'ADMGAR.LBL_MIN_COMPRA_LEASING', sort: false },
      { field: 'maximoCompraLeasing', header: 'ADMGAR.LBL_MAX_COMPRA_LEASING', sort: false },
      { field: 'fechaActivacion', header: 'ADMGAR.LBL_ACTIVACION', sort: false },
      { field: 'fechaInactivacion', header: 'ADMGAR.LBL_INACTIVACION', sort: false },
      { field: 'indicadorHabilitado', header: 'LBL_ESTADO', sort: false }
    ];
    if (this.mostrarBtnAcciones) {
      this.columnas.push({ field: 'acciones', header: 'LBL_ACCIONES', sort: false });
    }
  }

  private cargarDatos(): void {
    this.cargando = true;
    this.garantiaIdoneaService.listado().subscribe({
      next: (data) => {
        this.registros = data || [];
        this.registrosFiltrados = [...this.registros];
        this.cargando = false;
      },
      error: () => {
        this.cargando = false;
      }
    });
  }

  private cargarListas(): void {
    this.lisService.getMasterDetails('ListasModalidadesGarantias').subscribe({
      next: (data: ListaItem[]) => {
        this.lisModalidades = data.map(t => ({ label: t.nombre, value: t.idLista }));
      }
    });
  }

  buscar(valor: string): void {
    this.busqueda = valor;
    if (valor.length === 0 || valor.length >= 3) {
      this.tabla.filterGlobal(valor, 'contains');
    }
  }

  crear(): void {
    this.modoEdicion = false;
    this.registroEditando = null;
    this.errorDuplicado = false;
    this.submitted = false;
    this.tabEditarActiva = 0;
    this.inicializarFormulario();
    this.mostrarModal = true;
  }

  editar(row: GarantiasIdoneasModalidades): void {
    this.modoEdicion = true;
    this.registroEditando = row;
    this.errorDuplicado = false;
    this.submitted = false;
    this.tabEditarActiva = 0;
    this.tabAnterior = 0;
    this.historialCambios = [];
    this.inicializarFormulario(row);
    this.mostrarModal = true;
    if (this.mostrarTabHistorial) {
      this.cargarHistorialCambios(row.idGarantiaIdoneaModalidad);
    }
  }

  /**
   * Consulta el historial de cambios habilitados para la garantía idónea indicada.
   * @param idGarantiaIdoneaModalidad Identificador UUID de la garantía idónea modalidad
   */
  private cargarHistorialCambios(idGarantiaIdoneaModalidad: string): void {
    this.cargandoHistorial = true;
    this.historialService.listadoHabilitadosPorGarantiaIdoneaModalidad(idGarantiaIdoneaModalidad).subscribe({
      next: (registros) => {
        this.historialCambios = registros ?? [];
        this.cargandoHistorial = false;
      },
      error: () => {
        this.historialCambios = [];
        this.cargandoHistorial = false;
      }
    });
  }

  private inicializarFormulario(reg?: GarantiasIdoneasModalidades): void {
    // Limpiar subscripciones anteriores de valueChanges
    this.subscripciones.forEach(s => s.unsubscribe());
    this.subscripciones = [];
    this.errorDuplicado = false;
    this.errorMinMaxLeasing = false;

    // Reiniciar listas dependientes
    this.arbolTipos = [];
    this.lisTiposGarantia = [];
    this.lisClasesGarantia = [];
    this.lisClasesGarantiaTodas = [];
    this.lisBienesDerechos = [];

    this.formulario = this.formBuilder.group({
      idModalidadGarantia: [reg?.idModalidadGarantia ?? null, Validators.required],
      idTipoGarantia: [null, Validators.required],
      idOtraGarantia: [null, Validators.required],
      idBienDeDerecho: [null, Validators.required],
      endeudamientoSMLV: [reg?.endeudamientoSMLV ?? null, Validators.required],
      maximoFinanciar: [reg?.maximoFinanciar ?? null, Validators.required],
      minimoCompraLeasing: [reg?.minimoCompraLeasing ?? null],
      maximoCompraLeasing: [reg?.maximoCompraLeasing ?? null],
      indicadorHabilitado: [reg?.indicadorHabilitado ?? true, Validators.required]
    });

    this.configurarCascadaDropdowns();
    this.actualizarValidadoresLeasing();

    // En modo edición, cargar el árbol para la modalidad existente y pre-seleccionar dependientes
    if (reg?.idModalidadGarantia) {
      this.garantiaService.obtenerArbolPorModalidad(reg.idModalidadGarantia).subscribe({
        next: (arbol: TipoGarantiaTree[]) => {
          this.arbolTipos = arbol || [];
          this.lisTiposGarantia = this.arbolTipos.map(t => ({
            label: t.tipoGarantia,
            value: t.idTipoGarantia
          }));

          // Buscar en el árbol el nodo que coincida con el idGarantia del registro
          this.preseleccionarDesdeArbol(reg.idGarantia);
        }
      });
    }
  }

  /**
   * Recorre el árbol de tipos para encontrar el BienDeDerecho cuyo idGarantia coincida,
   * y pre-selecciona tipo, clase y bien/derecho sin disparar la cascada.
   */
  private preseleccionarDesdeArbol(idGarantia: string): void {
    for (const tipo of this.arbolTipos) {
      for (const otra of (tipo.otras || [])) {
        for (const bien of (otra.bienes || [])) {
          if (bien.idGarantia === idGarantia) {
            // Poblar lista de clases para este tipo
            this.lisClasesGarantia = tipo.otras.map(o => ({
              label: o.otraGarantia,
              value: o.idOtraGarantia
            }));
            this.lisClasesGarantiaTodas = tipo.otras;

            // Poblar lista de bienes para esta clase
            this.lisBienesDerechos = otra.bienes.map(b => ({
              label: b.bienDeDerecho,
              value: b.idBienDeDerecho
            }));

            // Setear valores sin emitir eventos para no disparar la cascada
            this.formulario.get('idTipoGarantia').setValue(tipo.idTipoGarantia, { emitEvent: false });
            this.formulario.get('idOtraGarantia').setValue(otra.idOtraGarantia, { emitEvent: false });
            this.formulario.get('idBienDeDerecho').setValue(bien.idBienDeDerecho, { emitEvent: false });

            this.actualizarValidadoresLeasing();
            return;
          }
        }
      }
    }
  }

  /**
   * Configura las subscripciones de valueChanges para la dependencia en cascada:
   * Modalidad → Tipo Garantía → Clase Garantía → Bien/Derecho
   */
  private configurarCascadaDropdowns(): void {
    // Modalidad → carga árbol y pobla Tipo Garantía
    this.subscripciones.push(
      this.formulario.get('idModalidadGarantia').valueChanges.subscribe(idModalidad => {
        // Limpiar dependientes
        this.formulario.get('idTipoGarantia').setValue(null, { emitEvent: true });
        this.lisTiposGarantia = [];
        this.lisClasesGarantia = [];
        this.lisClasesGarantiaTodas = [];
        this.lisBienesDerechos = [];
        this.arbolTipos = [];

        if (idModalidad) {
          this.garantiaService.obtenerArbolPorModalidad(idModalidad).subscribe({
            next: (arbol: TipoGarantiaTree[]) => {
              this.arbolTipos = arbol || [];
              this.lisTiposGarantia = this.arbolTipos.map(t => ({
                label: t.tipoGarantia,
                value: t.idTipoGarantia
              }));
            }
          });
        }
      })
    );

    // Tipo Garantía → pobla Clase Garantía (Otra Garantía)
    this.subscripciones.push(
      this.formulario.get('idTipoGarantia').valueChanges.subscribe(idTipo => {
        // Limpiar dependientes
        this.formulario.get('idOtraGarantia').setValue(null, { emitEvent: true });
        this.lisClasesGarantia = [];
        this.lisBienesDerechos = [];
        this.lisClasesGarantiaTodas = [];

        if (idTipo) {
          const tipoSeleccionado = this.arbolTipos.find(t => t.idTipoGarantia === idTipo);
          if (tipoSeleccionado?.otras) {
            this.lisClasesGarantia = tipoSeleccionado.otras.map(o => ({
              label: o.otraGarantia,
              value: o.idOtraGarantia
            }));
            this.lisClasesGarantiaTodas = tipoSeleccionado.otras;
          }
        }
        this.actualizarValidadoresLeasing();
      })
    );

    // Clase Garantía → pobla Bien/Derecho
    this.subscripciones.push(
      this.formulario.get('idOtraGarantia').valueChanges.subscribe(idClase => {
        // Limpiar dependiente
        this.formulario.get('idBienDeDerecho').setValue(null);
        this.lisBienesDerechos = [];

        if (idClase) {
          const idTipo = this.formulario.get('idTipoGarantia').value;
          const tipoSeleccionado = this.arbolTipos.find(t => t.idTipoGarantia === idTipo);
          const claseSeleccionada = tipoSeleccionado?.otras?.find(o => o.idOtraGarantia === idClase);
          if (claseSeleccionada?.bienes) {
            this.lisBienesDerechos = claseSeleccionada.bienes.map(b => ({
              label: b.bienDeDerecho,
              value: b.idBienDeDerecho
            }));
          }
        }
      })
    );
  }

  get f() { return this.formulario.controls; }

  cerrarModal(): void {
    if (this.formulario.dirty) {
      this.confirmacionServicio.confirm({
        key: 'unsaved_idonea',
        header: 'Cambios sin guardar',
        message: '¿Está seguro de salir sin guardar cambios?',
        accept: () => {
          this.mostrarModal = false;
          this.submitted = false;
        }
      });
    } else {
      this.mostrarModal = false;
      this.submitted = false;
    }
  }

  cancelar(): void {
    this.cerrarModal();
  }

  checkCamposEditando() {
    // Esta validación se hace porque si el apl papá cambió y ya no es posible encontrar estos 3 campos
    // entonces NO bloqueamos al usuario sino que mantenemos el guardado que es por idGarantia.
    if (this.modoEdicion && this.registroEditando && this.registroEditando.idGarantia) {
      const config = { emitEvent: false, onlySelf: true };
      this.f.idTipoGarantia.setValidators([]);
      this.f.idOtraGarantia.setValidators([]);
      this.f.idBienDeDerecho.setValidators([]);
      this.f.idTipoGarantia.updateValueAndValidity(config);
      this.f.idOtraGarantia.updateValueAndValidity(config);
      this.f.idBienDeDerecho.updateValueAndValidity(config);
    }
  }

  guardar(): void {
    this.submitted = true;
    this.actualizarValidadoresLeasing();
    this.checkCamposEditando();
    if (this.formulario.invalid) return;

    this.errorDuplicado = false;
    this.errorMinMaxLeasing = false;
    this.guardando = true;
    const formValues = this.formulario.value;

    // Validación min max leasing
    if (this.esLeasing) {
      const min = Number(formValues.minimoCompraLeasing);
      const max = Number(formValues.maximoCompraLeasing);
      if (min > max) {
        this.errorMinMaxLeasing = true;
        this.guardando = false;
        return;
      }
    }

    // Obtener idGarantia desde el árbol basado en idBienDeDerecho
    let idGarantia = null;
    const tipoSeleccionado = this.arbolTipos.find(t => t.idTipoGarantia === formValues.idTipoGarantia);
    const claseSeleccionada = tipoSeleccionado?.otras?.find(o => o.idOtraGarantia === formValues.idOtraGarantia);
    const bienSeleccionado = claseSeleccionada?.bienes?.find(b => b.idBienDeDerecho === formValues.idBienDeDerecho);
    idGarantia = bienSeleccionado?.idGarantia;

    // Validación de duplicado (solo al crear)
    if (!this.modoEdicion) {
      const existe = this.registros.some(r => r.idGarantia === idGarantia && r.idModalidadGarantia === formValues.idModalidadGarantia);
      if (existe) {
        this.errorDuplicado = true;
        this.guardando = false;
        return;
      }
    } else if (!idGarantia && this.registroEditando) {
      idGarantia = this.registroEditando.idGarantia;
    }

    // Preparar el payload real
    const payload: GarantiasIdoneasModalidades = {
      ...(this.registroEditando || {}),
      idGarantia: idGarantia,
      idModalidadGarantia: formValues.idModalidadGarantia,
      endeudamientoSMLV: formValues.endeudamientoSMLV,
      maximoFinanciar: formValues.maximoFinanciar,
      minimoCompraLeasing: formValues.minimoCompraLeasing,
      maximoCompraLeasing: formValues.maximoCompraLeasing,
      indicadorHabilitado: formValues.indicadorHabilitado
    } as GarantiasIdoneasModalidades;

    if (this.modoEdicion) {
      if (this.registroEditando.indicadorHabilitado !== formValues.indicadorHabilitado) {
        if (formValues.indicadorHabilitado) {
          payload.fechaActivacion = moment().format('YYYY-MM-DD[T]HH:mm:ss.SSSZZ') as any;
        } else {
          payload.fechaInactivacion = moment().format('YYYY-MM-DD[T]HH:mm:ss.SSSZZ') as any;
        }
      }
      const historialPayload = this.construirHistorialCambios(payload.idGarantiaIdoneaModalidad, formValues);

      this.garantiaIdoneaService.actualizar(payload).pipe(
        switchMap(() =>
          historialPayload.length > 0
            ? this.historialService.crearMasivo(historialPayload)
            : of(null)
        )
      ).subscribe({
        next: () => {
          if (!payload.indicadorHabilitado && this.registroEditando.indicadorHabilitado) this.garantiaIdoneaService.envioNotification(payload.idGarantiaIdoneaModalidad).subscribe();
          this.formulario.markAsPristine();
          this.guardando = false;
          this.mostrarModal = false;
          this.cargarDatos();
        },
        error: () => {
          this.guardando = false;
        }
      });
    } else {
      if (formValues.indicadorHabilitado) {
        payload.fechaActivacion = moment().format('YYYY-MM-DD[T]HH:mm:ss.SSSZZ') as any;
      } else {
        payload.fechaInactivacion = moment().format('YYYY-MM-DD[T]HH:mm:ss.SSSZZ') as any;
      }
      this.garantiaIdoneaService.crear(payload).pipe(
        switchMap(nueva => {
          const historialPayload = this.construirHistorialCreacion(nueva.idGarantiaIdoneaModalidad, formValues);
          return historialPayload.length > 0
            ? this.historialService.crearMasivo(historialPayload).pipe(map(() => nueva))
            : of(nueva);
        })
      ).subscribe({
        next: () => {
          this.formulario.markAsPristine();
          this.guardando = false;
          this.mostrarModal = false;
          this.cargarDatos();
        },
        error: () => {
          this.guardando = false;
        }
      });
    }
  }

  onCambiarTab(event: any): void {
    const tabNueva = event.index;
    if (this.formulario?.dirty && tabNueva !== 0) {
      this.tabEditarActiva = 0;
    } else {
      this.tabAnterior = tabNueva;
    }
  }

  get esLeasing() {
    if (this.f?.idOtraGarantia.value) {
      const reg = this.lisClasesGarantiaTodas.find(t => t.idOtraGarantia === this.f.idOtraGarantia.value);
      return reg && reg.codigoOtraGarantia === CodigosGarantias.LEASIN;
    } else {
      return false;
    }
  }

  /**
   * Actualiza dinámicamente los validadores de los campos de leasing
   * según si la clase seleccionada es Leasing o no.
   */
  private actualizarValidadoresLeasing(): void {
    const minControl = this.formulario.get('minimoCompraLeasing');
    const maxControl = this.formulario.get('maximoCompraLeasing');

    if (this.esLeasing) {
      minControl.setValidators([Validators.required]);
      maxControl.setValidators([Validators.required]);
    } else {
      minControl.clearValidators();
      maxControl.clearValidators();
    }

    minControl.updateValueAndValidity();
    maxControl.updateValueAndValidity();
  }

  /**
   * Compara los valores actuales del formulario con los datos originales del registro
   * y retorna el arreglo de cambios para registrar en el historial.
   */
  private construirHistorialCambios(
    idGarantiaIdoneaModalidad: string,
    formValue: any
  ): GarantiaIdoneaModalidadHistorialCambio[] {
    const cambios: GarantiaIdoneaModalidadHistorialCambio[] = [];

    const formatHabilitado = (v: any): string => v === true ? 'Habilitado' : v === false ? 'Deshabilitado' : '';

    const resolverLabel = (campo: string, valor: any): string => {
      if (valor === null || valor === undefined) { return ''; }
      switch (campo) {
        case 'idModalidadGarantia': return this.lisModalidades.find(x => x.value === valor)?.label ?? String(valor);
        case 'idTipoGarantia':     return this.lisTiposGarantia.find(x => x.value === valor)?.label ?? String(valor);
        case 'idOtraGarantia':    return this.lisClasesGarantia.find(x => x.value === valor)?.label ?? String(valor);
        case 'idBienDeDerecho':      return this.lisBienesDerechos.find(x => x.value === valor)?.label ?? String(valor);
        default:                   return String(valor);
      }
    };

    const CAMPOS_ID         = new Set(['idModalidadGarantia', 'idTipoGarantia', 'idOtraGarantia', 'idBienDeDerecho']);
    const CAMPOS_HABILITADO = new Set(['indicadorHabilitado']);
    const CAMPOS_SMLV = new Set(['endeudamientoSMLV'])

    Object.keys(this.HISTORIAL_CAMPO_LABEL).forEach(campo => {
      const anterior = (this.registroEditando as any)[campo];
      const nuevo    = formValue[campo];
      if (anterior === nuevo) { return; }

      let valorAnterior: string;
      let valorNuevo: string;

      if (CAMPOS_ID.has(campo)) {
        valorAnterior = resolverLabel(campo, anterior);
        valorNuevo    = resolverLabel(campo, nuevo);
      } else if (CAMPOS_HABILITADO.has(campo)) {
        valorAnterior = formatHabilitado(anterior);
        valorNuevo    = formatHabilitado(nuevo);
      } else if (CAMPOS_SMLV.has(campo)) {
        valorAnterior =  this.dineroPipe.transform((this.salario * anterior));
        valorNuevo    =  this.dineroPipe.transform((this.salario * nuevo));
      } else {
        valorAnterior = anterior != null ? String(anterior) : '';
        valorNuevo    = nuevo != null ? String(nuevo) : '';
      }

      cambios.push({
        idGarantiaIdoneaModalidad,
        campo: this.HISTORIAL_CAMPO_LABEL[campo],
        valorAnterior,
        valorNuevo
      } as GarantiaIdoneaModalidadHistorialCambio);
    });

    return cambios;
  }

  /**
   * Construye el historial de creación para un registro nuevo.
   * Todos los campos rastreados se registran con valorAnterior = '-'.
   */
  private construirHistorialCreacion(
    idGarantiaIdoneaModalidad: string,
    formValue: any
  ): GarantiaIdoneaModalidadHistorialCambio[] {
    const cambios: GarantiaIdoneaModalidadHistorialCambio[] = [];

    const formatHabilitado = (v: any): string => v === true ? 'Habilitado' : v === false ? 'Deshabilitado' : '';

    const resolverLabel = (campo: string, valor: any): string => {
      if (valor === null || valor === undefined) { return ''; }
      switch (campo) {
        case 'idModalidadGarantia': return this.lisModalidades.find(x => x.value === valor)?.label ?? String(valor);
        case 'idTipoGarantia':     return this.lisTiposGarantia.find(x => x.value === valor)?.label ?? String(valor);
        case 'idOtraGarantia':    return this.lisClasesGarantia.find(x => x.value === valor)?.label ?? String(valor);
        case 'idBienDeDerecho':      return this.lisBienesDerechos.find(x => x.value === valor)?.label ?? String(valor);
        default:                   return String(valor);
      }
    };

    const CAMPOS_ID         = new Set(['idModalidadGarantia', 'idTipoGarantia', 'idOtraGarantia', 'idBienDeDerecho']);
    const CAMPOS_HABILITADO = new Set(['indicadorHabilitado']);
    const CAMPOS_SMLV = new Set(['endeudamientoSMLV'])

    Object.keys(this.HISTORIAL_CAMPO_LABEL).forEach(campo => {
      const nuevo = formValue[campo];
      let valorNuevo: string;

      if (CAMPOS_ID.has(campo)) {
        valorNuevo = resolverLabel(campo, nuevo);
      } else if (CAMPOS_HABILITADO.has(campo)) {
        valorNuevo = formatHabilitado(nuevo);
      } else if (CAMPOS_SMLV.has(campo)) {
        valorNuevo    = this.dineroPipe.transform((this.salario * nuevo));
      } else {
        valorNuevo = nuevo != null ? String(nuevo) : '-';
      }

      if (!valorNuevo) valorNuevo = '-';

      cambios.push({
        idGarantiaIdoneaModalidad,
        campo: this.HISTORIAL_CAMPO_LABEL[campo],
        valorAnterior: '-',
        valorNuevo
      } as GarantiaIdoneaModalidadHistorialCambio);
    });

    return cambios;
  }

}
